
import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { FeedbackTicket } from '../types';

export const useFeedbackTickets = () => {
  const [tickets, setTickets] = useState<FeedbackTicket[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const isUpdatingRef = useRef<boolean>(false);

  const loadTickets = async () => {
    // Skip loading if we're already updating a ticket status
    // This prevents UI freeze by avoiding simultaneous data loading operations
    if (isUpdatingRef.current) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Call the Supabase function to get the tickets
      // This bypasses the RLS policies that are causing the infinite recursion
      const { data: feedbackData, error: feedbackError } = await supabase.functions.invoke('get_feedback_tickets');

      if (feedbackError) {
        console.error('Error loading tickets:', feedbackError);
        throw feedbackError;
      }

      if (!feedbackData || !Array.isArray(feedbackData.tickets)) {
        console.error('Invalid data returned from get_feedback_tickets function');
        throw new Error('Invalid data returned from server');
      }
      
      setTickets(feedbackData.tickets);
    } catch (error) {
      console.error('Error loading tickets:', error);
      setError(error as Error);
      toast.error('Failed to load feedback tickets');
    } finally {
      setLoading(false);
    }
  };

  // Load feedback tickets on component mount
  useEffect(() => {
    loadTickets();
  }, []);

  const updateTicketStatus = async (id: string, status: 'Open' | 'In Progress' | 'Resolved') => {
    try {
      // Set the updating flag to prevent multiple loading operations
      isUpdatingRef.current = true;
      
      // Use the function to update the ticket status
      const { error } = await supabase.functions.invoke('update_ticket_status', {
        body: JSON.stringify({ id, status })
      });

      if (error) {
        throw error;
      }

      // Update local state to improve responsiveness
      setTickets((prev: FeedbackTicket[]) => prev.map((ticket: FeedbackTicket) =>
        ticket.id === id ? { ...ticket, status } : ticket
      ));
      
      toast.success(`Ticket status updated to ${status}`);
      
      // Wait a brief moment before allowing further updates
      setTimeout(() => {
        isUpdatingRef.current = false;
      }, 500);
      
      return true;
    } catch (error) {
      console.error('Error updating ticket status:', error);
      toast.error('Failed to update ticket status');
      isUpdatingRef.current = false;
      return false;
    }
  };

  const refreshTickets = () => {
    // Only refresh if we're not already updating
    if (!isUpdatingRef.current) {
      loadTickets();
    }
  };

  return { tickets, loading, error, refreshTickets, updateTicketStatus };
};
